import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On app load — restore user from token
  useEffect(() => {
    const token = localStorage.getItem('sambal_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchMe();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchMe = async () => {
    try {
      const { data } = await api.get('/auth/me');
      // Backend returns { success: true, data: user }
      setUser(data.data);
    } catch (err) {
      // Only clear token if it's actually a 401 (invalid/expired token)
      // NOT on network errors — so page refresh on bad network doesn't logout
      if (err.response?.status === 401) {
        localStorage.removeItem('sambal_token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
      // If network error (no response), keep token — user stays logged in
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('sambal_token', data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    let authenticatedUser = data.user;

    // The login response can be intentionally compact. Refresh the signed-in
    // user so role permissions are available before protected pages render.
    try {
      const meResponse = await api.get('/auth/me');
      authenticatedUser = meResponse.data?.data || authenticatedUser;
    } catch {
      // Keep a successful login usable if the follow-up request temporarily fails.
      // Permission checks are deny-by-default when permissions are unavailable.
    }

    setUser(authenticatedUser);
    toast.success(`Welcome back, ${authenticatedUser.name}!`);
    return authenticatedUser;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await api.post('/auth/register', formData);
    localStorage.setItem('sambal_token', data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
    toast.success('पंजीकरण सफल! Welcome to SAMBAL Trust.');
    return { user: data.user };
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('sambal_token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'subadmin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, isAdmin, isSuperAdmin, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
